/**
 * Top bar (P1-T11, restyled 2026-10-17): Loomia logo + ✦ credit-balance pill
 * with a "+" (opens the paywall). Settings now lives under the Profile tab.
 * Guests see a "Sign in" button instead of the pill.
 * The pill retries the balance fetch when the balance failed to load.
 */
import { creditsChanged, getBalance, refreshCredits } from '../state/credits.js';
import { navigate } from '../router.js';
import { hasAccount, setReturnTo } from '../state/auth.js';
import { dailyRewardClaimable, openDailyReward } from '../ui/dailyReward.js';
import { icon } from './icon.js';

const LOGO = `
  <a class="brand" href="#/home" aria-label="Loomia home">
    <img src="/img/logo.webp" alt="Loomia" width="500" height="129" decoding="async" />
  </a>`;

/** Render into `host`. Returns a cleanup function. */
export function renderTopBar(host: HTMLElement): () => void {
  if (!hasAccount()) return renderGuestTopBar(host);
  host.innerHTML = `
    <div class="topbar-inner">
      ${LOGO}
      <div class="topbar-right">
        <button class="icon-btn gift-btn" type="button" aria-label="Daily reward" data-gift>${icon('redeem', { fill: true })}<span class="dot" hidden></span></button>
        <button class="balance-pill" type="button" aria-label="Credit balance — open paywall">
          ${icon('stars', { fill: true, cls: 'balance-star' })}
          <span id="balance-value">…</span>
          ${icon('add', { cls: 'balance-plus' })}
        </button>
      </div>
    </div>
  `;

  const valueEl = host.querySelector<HTMLElement>('#balance-value');
  const pill = host.querySelector<HTMLButtonElement>('.balance-pill');

  const paint = (): void => {
    if (!valueEl) return;
    const b = getBalance();
    valueEl.textContent = b === null ? '—' : String(b);
  };
  paint();
  const off = creditsChanged.on(paint);

  const onTap = (): void => {
    if (getBalance() === null) {
      // Balance unknown (failed fetch): retry instead of opening the paywall.
      void refreshCredits();
    } else {
      navigate('/paywall');
    }
  };
  pill?.addEventListener('click', onTap);
  const stopGift = wireGift(host);

  return () => {
    off();
    stopGift();
    pill?.removeEventListener('click', onTap);
  };
}

/** Guests see a "Sign in" button instead of the balance pill. */
function renderGuestTopBar(host: HTMLElement): () => void {
  host.innerHTML = `
    <div class="topbar-inner">
      ${LOGO}
      <div class="topbar-right">
        <button class="icon-btn gift-btn" type="button" aria-label="Daily reward" data-gift>${icon('redeem', { fill: true })}<span class="dot" hidden></span></button>
        <button class="btn btn-primary" type="button" id="topbar-signin" style="padding: 8px 16px; min-height: 0;">
          Sign in
        </button>
      </div>
    </div>
  `;
  const btn = host.querySelector<HTMLButtonElement>('#topbar-signin');
  const onTap = (): void => {
    setReturnTo(window.location.hash);
    navigate('/login');
  };
  btn?.addEventListener('click', onTap);
  const stopGift = wireGift(host);
  return () => {
    stopGift();
    btn?.removeEventListener('click', onTap);
  };
}

/** Gift button: opens the Daily Reward sheet; a red dot shows when a claim is waiting. */
function wireGift(host: HTMLElement): () => void {
  const gift = host.querySelector<HTMLButtonElement>('[data-gift]');
  const dot = gift?.querySelector<HTMLElement>('.dot');
  const open = (): void => void openDailyReward();
  const hideDot = (): void => {
    if (dot) dot.hidden = true;
  };
  gift?.addEventListener('click', open);
  document.addEventListener('loomia:daily-claimed', hideDot);
  void dailyRewardClaimable().then((v) => {
    if (dot) dot.hidden = !v;
  });
  return () => {
    gift?.removeEventListener('click', open);
    document.removeEventListener('loomia:daily-claimed', hideDot);
  };
}
